/**
 * @Author: v_jppjpeng
 * @Date: 2022-10-26 10:52:30
 * @Explain: 邮箱模板 - 变量 和 函数
 */

import { CONTACT_MANAGE_FIELD_NAME_MAX_LEN } from '@/config/defaultConfig';
import { intlGet, intlGetLanguage } from '@/locales';
// utils
import { jsonParse } from '@/utils/global';
import { onPattern, pattern } from '@/utils/regular';
// type
import type { MailTemplateListItem } from '@/api/mailTemplate/data.d';
import type { TemplateSelectItem } from '@/components/common/templateSelect';
import {
  ContactVarPrefix,
  KeyVarPrefix,
  LINKVARIABLE,
} from '@/components/emailEditor/components/data.d';
import type {
  PromptMappingTableType,
  TdPromptMappingTableType,
} from '@/definition/common/data.d';
import { InputEnum } from '@/definition/common/data.d';
import { PlaceholderTypeEnum } from '@/pages/mailTemplate/utils/enum/addOrEdit';
import { v4 as uuidv4 } from 'uuid';
import type { FormDataType, TransFormDataType } from './data.d';
import { DefaultSelectKey, SelectAndUpdateRight } from './data.d';

// 添加模板 - 默认表单
export const defaultForm: FormDataType = {
  tag: '',
  mode: '',
  content: '',
  lang: '',
  isRichText: 1,
  isUnsubscribed: undefined, // 默认 undefined
  editorType: undefined, // 默认 undefined
  mjmlContent: undefined, // 默认 undefined
  // 是否开启多语言
  isMuilityLanguage: 0,
  muilityLangContent: [],
  /** 无默认语言触发开启多语言按钮时，默认语言选框需高亮标识 */
  defaultLangHightLight: false,
};

// 添加模板 - 默认表单
export const defaultTransForm: TransFormDataType = {
  tag: '',
  template_id: undefined,
  // 分组 id
  groupUuid: undefined,
  /** 发件人 */
  send_name: '',
  mode: '',
  editorType: undefined, // 默认 undefined
  mjmlContent: undefined, // 默认 undefined
  /** 地址前缀 */
  address_prefix: '',
  /** 域名 */
  domain: '',
  /** 主题 */
  send_subject: '',
  /** 验证邮箱内容 */
  email: '',

  /** 语言 */
  lang: '',
  /** 邮件内容 */
  content: '',
  /** 预览文本 */
  preview_text: '',
};

// 根据字段映射表
export const promptMappingTable: PromptMappingTableType[] = [
  // 模板名称
  {
    inputType: InputEnum.input,
    field: 'tag',
    labelKey: 'TEMPLATE.MANAGE.TEMPLATE.NAME',
    msgKey: 'TEMPLATE.MANAGE.PLEASE.ENTER.A.TEMPLATE.NAME',
    required: true,
    align: 'middle',
  },
  // 语言
  {
    inputType: InputEnum.select,
    field: 'lang',
    labelKey: 'TEMPLATE.MANAGE.TEMPLATE.LANGUAGE',
    msgKey: 'TEMPLATE.MANAGE.PLEASE.SELECT.TEMPLATE.LANGUAGE',
    required: true,
    align: 'middle',
  },
  // 偏好设置
  {
    inputType: InputEnum.select,
    field: 'subscribeStatus',
    msgKey: 'TEMPLATE.MANAGE.PLEASE.SELECT.SUBSCRIBESTATUS',
    required: false,
    align: 'middle',
  },
  // 上传时候的提示
  {
    inputType: InputEnum.custom,
    field: 'content',
    msgKey: 'TEMPLATE.MANAGE.PLEASE.UPLOAD.HTML.FILE',
  },
  // 输入时候的提示
  {
    inputType: InputEnum.custom,
    field: 'contentInput',
    msgKey: 'TEMPLATE.MANAGE.PLEASE.FILL.IN.THE.CODE.CONTENT',
  },
];

export const TEXT_EDITOR_COLOR = [
  '#000000',
  '#333333',
  '#666666',
  '#999999',
  '#cccccc',
  '#ffffff',
  '#61a951',
  '#16a085',
  '#07a9fe',
  '#003ba5',
  '#8e44ad',
  '#f32784',
  '#c0392b',
  '#d35400',
  '#f39c12',
  '#fdda00',
  '#7f8c8d',
  '#2c3e50',
];

// Select 更新表单信息
export const tdPromptMappingTable: TdPromptMappingTableType[] = [
  {
    inputType: InputEnum.input,
    field: 'tag',
    labelKey: 'FIELD.SETTINGS.NAME',
    placeholderKey: 'FIELD.SETTINGS.NAME',
    contentProps: {
      maxlength: CONTACT_MANAGE_FIELD_NAME_MAX_LEN,
      showLimitNumber: true,
    },
    formRules: [
      {
        required: true,
        message: intlGet('FIELD.SETTINGS.PLEASE.ENTER.NAME'),
        type: 'error',
      },
      // {
      //   whitespace: true,
      //   messageKey: 'FIELD.SETTINGS.PLEASE.ENTER.NAME',
      //   type: 'error',
      // },
      {
        validator: async (val: string) => {
          const checkStatus = val ? onPattern(val, pattern.isSpace) : false;
          let validateRes = { result: true, message: '', type: 'success' };

          if (!checkStatus) {
            validateRes = {
              result: false,
              message: intlGet('TEMPLATE.MANAGE.FOLDER.NAME.BLANK.SPACE'),
              type: 'error',
            };
          }

          return validateRes;
        },
      },
    ],
  },
];

// 文件夹 所有 和 未分组 value值
export const ALL = -1;
export const UNGROUPED = -2;
// 文件夹list内容处理
export const folderListDeal = (params: any) => {
  const { arr = [], unChangeEditStatus } = params;

  return arr.map((item: any) => {
    const obj = {
      value: item.id,
      text: item.tag,
      content: item.count,
      rightType: SelectAndUpdateRight.CONTENT,
    };
    switch (item.groupType) {
      case DefaultSelectKey.ALL:
        return {
          ...item,
          ...obj,
          value: ALL,
          text: intlGet('COMMON.ALL'),
          unChangeEditStatus: unChangeEditStatus ? DefaultSelectKey.ALL : 0,
        };
      case DefaultSelectKey.UNGROUPED:
        return {
          ...item,
          ...obj,
          value: UNGROUPED,
          text: intlGet('TEMPLATE.MANAGE.FOLDER.NAME.UNGROUPED'),
          unChangeEditStatus: unChangeEditStatus
            ? DefaultSelectKey.UNGROUPED
            : 0,
        };
      default:
        return { ...item, ...obj };
    }
  });
};

// 事务文件夹list内容处理
export const transFolderListDeal = (params: any) => {
  const { arr = [], unChangeEditStatus } = params;

  return arr.map((item: any) => {
    const obj = {
      value: item.groupUuid,
      text: item.groupName,
      content: item.num,
      rightType: SelectAndUpdateRight.CONTENT,
    };
    switch (item.groupType) {
      case DefaultSelectKey.ALL:
        return {
          ...item,
          ...obj,
          value: ALL,
          text: intlGet('COMMON.ALL'),
          unChangeEditStatus: unChangeEditStatus ? DefaultSelectKey.ALL : 0,
        };
      case DefaultSelectKey.UNGROUPED:
        return {
          ...item,
          ...obj,
          value: UNGROUPED,
          text: intlGet('TEMPLATE.MANAGE.FOLDER.NAME.UNGROUPED'),
          unChangeEditStatus: unChangeEditStatus
            ? DefaultSelectKey.UNGROUPED
            : 0,
        };
      default:
        return { ...item, ...obj };
    }
  });
};

// 过滤变量
export const getFilterVar = (arr: any[]) =>
  arr.filter(
    (item) =>
      item !== 'unsubscribe_system' && item !== 'updatepreference_system',
  );

// 获取去重后的表单信息
export const activeFormDuplicateRemoval = (unactiveForm: any[]) => {
  // 获取去重后的表单信息
  const noRepeatUnactiveForm: any[] = [];
  unactiveForm.map((i: any) => {
    // 查询是否存在重复表单
    const hasSame = noRepeatUnactiveForm.some(
      (v) => v?.formUuid === i.formUuid,
    );
    // 不重复则保留
    if (!hasSame) {
      noRepeatUnactiveForm.push(i);
    }
  });
  return noRepeatUnactiveForm;
};

// 组合模板分组列表 - 用于分组下拉
export const combinedTempList = (list: MailTemplateListItem[]) => {
  const newTempList: TemplateSelectItem[] = [];
  const otherTempList: TemplateSelectItem[] = [];

  list?.forEach((item) => {
    // 获取去重后的表单信息
    const noRepeatUnactiveForm: any[] = activeFormDuplicateRemoval(
      item?.unactiveForm || [],
    );
    const newItem: TemplateSelectItem = {
      label: item.tag,
      value: item.id,
      group: item.lang,
      sortKey: intlGetLanguage(item.lang),
      /** 列表渲染需要的参数 */
      listRenderParams: {
        groupName: item.groupName,
        variableMap: jsonParse(item?.variableMap),
        isUnsubscribedLink: item?.isUnsubscribedLink,
        unactiveForm: noRepeatUnactiveForm,
      },
      /** 接口需要的参数 */
      addParams: {
        templateName: item.tag,
        variables: jsonParse(item.variables) || [],
        thumbnailUrl: item.thumbnailUrl,
      },
    };
    if (item.lang) {
      newTempList.push(newItem);
    } else {
      otherTempList.push({
        ...newItem,
        group: 'other',
        sortKey: 'z',
      });
    }
  });

  return {
    tempList: newTempList,
    otherTempList,
  };
};
//  a tag attr
export const sesTagAttr = 'ses:tags';
//  a tag attr prefix
export const sesTagAttrPrefix = 'template:';
// 校验sesTag内容 ses:tag label:value
const verifySesTag = (sesTag: string) => {
  return sesTag.split(':').length === 2;
};

//  添加链接标签
export const addLinkSesTag = (node: HTMLElement) => {
  const aElements = node.querySelectorAll('a');
  // tag 数组
  const tagArr = [];
  aElements.forEach((a) => {
    const tagVal = a.getAttribute(sesTagAttr);
    if (
      !tagVal ||
      (tagVal && (tagArr.includes(tagVal) || !verifySesTag(tagVal)))
    ) {
      // 重复 校验不通过的 tag重新生成
      const newTagVal = `${sesTagAttrPrefix}${uuidv4()}`;
      a.removeAttribute(sesTagAttr);
      a.setAttribute(sesTagAttr, newTagVal);
      tagArr.push(newTagVal);
    } else {
      tagArr.push(tagVal);
    }
  });
};

//  添加img alt 补充 默认 img
export const addImgAltAttr = (node: HTMLElement) => {
  const imgElements = node.querySelectorAll('img');
  imgElements.forEach((img) => {
    if (!img.getAttribute('alt')) {
      img.setAttribute('alt', 'img');
    }
  });
};

// 判断字符串是否包含特殊字符（数字 字母 下划线以外）
export const hasChinese = (str: string) => {
  return /[^a-zA-Z0-9_]/.test(str);
};

// 匹配英文字母、数字、短横线和下划线
// 判断Contact  也支持 _
export const verifyContactVar = (str: string) => {
  return /^[a-zA-Z0-9_-]+$/.test(str);
};

// 判断Key
export const verifyKeyVar = (str: string) => {
  return /^[a-zA-Z0-9_-]+$/.test(str);
};

// 判断其他变量
export const verifyOtherVar = (str: string) => {
  return /^[a-zA-Z0-9_-]+$/.test(str);
};

const contactVarPrefixStr = `${ContactVarPrefix}:`;
const KeyVarPrefixStr = `${KeyVarPrefix}:`;
const startVarStr = '{{';
const endVarStr = '}}';

export const formatVariable = (label: string) => {
  if(!label) return ''
  // 去掉 {{ }}
  const labelVal = label.substring(
    startVarStr.length,
    label.length - endVarStr.length,
  );
  return labelVal.startsWith(contactVarPrefixStr)
    ? labelVal.substring(contactVarPrefixStr.length)
    : labelVal.startsWith(KeyVarPrefixStr)
    ? labelVal.substring(KeyVarPrefixStr.length)
    : labelVal;
};

type variableType = {
  label: string;
  variable: string;
  value: string;
  type: PlaceholderTypeEnum;
};

type getTemplateVarType = {
  sourceVariableList: variableType[];
  concatVariableList: variableType[];
  contactVariableList: variableType[];
  otherVariableList: variableType[];
  existErrorWordsArr: variableType[];
  allVarList: variableType[];
};
// 获取占位符
export const getTemplateVar = (htmlStr: string): getTemplateVarType => {
  // 前端 dom解析
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlStr, 'text/html');
  // 需要识别属性 innerHTML
  const text: string = doc?.body?.innerHTML;
  // 占位符格式匹配
  // const matchesAll = text.match(
  //   /\{\{([^}]+)\}\}|Contact:\{\{([^}]+)\}\}|Key:\{\{([^}]+)\}\}/g,
  // );
  const matchesAll = text.match(/\{\{([^}]+)\}\}/g);

  const varList = [];
  if (matchesAll && Array.isArray(matchesAll) && matchesAll.length) {
    let loc = 1;
    matchesAll.forEach((item, index) => {
      // console.log('item', item);
      // 去掉内置变量
      if (
        item.length > 4 &&
        ![...LINKVARIABLE.LinkHrefOptions].some(
          (systemVarItem) => systemVarItem.hrefValue === item,
        )
      ) {
        // 去掉 {{ }}
        const labelVal = item.substring(
          startVarStr.length,
          item.length - endVarStr.length,
        );

        varList.push({
          label: item,
          variable: formatVariable(item),
          value: intlGet('TEMPLATE.MANAGE.MAIL.TEST.EMAIL.VAR.PHD', {
            index: loc++,
          }),
          type: labelVal.startsWith(contactVarPrefixStr)
            ? PlaceholderTypeEnum.CONTACT_FIELD
            : labelVal.startsWith(KeyVarPrefixStr)
            ? PlaceholderTypeEnum.SOURCE_CODE
            : PlaceholderTypeEnum.ALL,
        });
      }
    });
  }

  return {
    sourceVariableList:
      varList.filter((item) => item.type === PlaceholderTypeEnum.SOURCE_CODE) ??
      [],
    concatVariableList:
      varList.filter(
        (item) => item.type === PlaceholderTypeEnum.CONTACT_FIELD,
      ) ?? [],
    contactVariableList:
      varList.filter(
        (item) => item.type === PlaceholderTypeEnum.CONTACT_FIELD,
      ) ?? [],
    otherVariableList:
      varList.filter((item) => item.type === PlaceholderTypeEnum.ALL) ?? [],
    allVarList: varList,
    existErrorWordsArr:
      varList
        .filter((item) => {
          // 校验
          if (item.type === PlaceholderTypeEnum.CONTACT_FIELD) {
            return !verifyContactVar(item.variable);
          } else if (item.type === PlaceholderTypeEnum.SOURCE_CODE) {
            return !verifyKeyVar(item.variable);
          }
          return !verifyOtherVar(item.variable);
        })
        ?.map((item) => item.label) ?? [],
  };
};
// 获取过滤主题变量的默认值
export const getFilterSubjectDefaultAttributeValue = (options: {
  subject: string;
  subjectDefaultAttributeValue: object;
}): { keys: string[]; filterSubjectDefaultAttributeValue: object } => {
  const { contactVariableList } = getTemplateVar(options.subject);

  const subjectDefaultAttributeValueKeySet = new Set();
  contactVariableList.forEach((item) => {
    subjectDefaultAttributeValueKeySet.add(item.variable);
  });
  const keys = Array.from(subjectDefaultAttributeValueKeySet);
  const filterSubjectDefaultAttributeValue = {};
  keys.forEach((key) => {
    // 默认值 替换
    filterSubjectDefaultAttributeValue[key] =
      options.subjectDefaultAttributeValue[key] || '';
  });
  return {
    keys,
    filterSubjectDefaultAttributeValue,
  };
};
